import {
  EnvironmentProviders,
  InjectionToken,
  PLATFORM_ID,
  isDevMode,
  makeEnvironmentProviders,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { AppCheck as FbAppCheck } from 'firebase/app-check';
import {
  getLimitedUseToken as rawGetLimitedUseToken,
  getToken as rawGetToken,
  initializeAppCheck as rawInitializeAppCheck,
  onTokenChanged as rawOnTokenChanged,
  setTokenAutoRefreshEnabled as rawSetTokenAutoRefreshEnabled,
} from 'firebase/app-check';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';

// Wrapped firebase/app-check functions. Mirrors @angular/fire's
// _zoneWrap(fn, true) declarations from angular-fire-app-check.mjs.
export const getLimitedUseToken = kdvWrap(rawGetLimitedUseToken, true);
export const getToken = kdvWrap(rawGetToken, true);
export const initializeAppCheck = kdvWrap(rawInitializeAppCheck, true);
export const onTokenChanged = kdvWrap(rawOnTokenChanged, true);
export const setTokenAutoRefreshEnabled = kdvWrap(rawSetTokenAutoRefreshEnabled, true);

// Re-exports — provider classes (not wrappable; they're constructors).
export {
  ReCaptchaV3Provider,
  ReCaptchaEnterpriseProvider,
  CustomProvider,
} from 'firebase/app-check';
export type { AppCheckTokenResult, AppCheckOptions } from 'firebase/app-check';

export type AppCheck = FbAppCheck;
export const AppCheck = new InjectionToken<FbAppCheck | null>('@fido/firebase.app-check');

const LOCALHOSTS = ['localhost', '0.0.0.0', '127.0.0.1'];

declare global {
  // eslint-disable-next-line no-var
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

export function provideAppCheck(factory: () => FbAppCheck): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: AppCheck,
      // Mirrors @angular/fire's appCheckInstanceFactory — see angular-fire-app-check.mjs.
      // - Sets globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = true in dev mode or
      //   on localhost so Firebase uses a debug token instead of attempting
      //   real ReCAPTCHA (which would fail against localhost / fido-emulator).
      // - FirebaseApp dep forces initializeApp() before this factory runs.
      // - PLATFORM_ID dep gives us the SSR fallback (return null) so downstream
      //   services keep working without an App Check token.
      useFactory: (_app: unknown, platformId: object) => {
        if (!isPlatformBrowser(platformId)) return null;
        const isLocalhost =
          typeof window !== 'undefined' && LOCALHOSTS.includes(window.location.hostname);
        if (isDevMode() || isLocalhost) {
          globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN ??= true;
        }
        return factory();
      },
      deps: [FirebaseApp, PLATFORM_ID],
    },
  ]);
}
